import type { SupabaseClient } from '../client'
import type { AccessLevel } from '../constants'
import { SupabaseError } from '../errors'

/**
 * O nível da pessoa logada nesta iniciativa, e quem faz o que ela não pode.
 *
 * A tela precisa das duas respostas: sem a primeira ela oferece o que o banco
 * recusa; sem a segunda ela esconde sem explicar, e quem procura o botão que
 * sumiu conclui que o painel quebrou.
 */

export type ColegaQuePode = {
  id: string
  name: string
  email: string
  access_level: AccessLevel
}

export async function getMeuNivelNaIniciativa(
  client: SupabaseClient,
  initiativeId: number,
): Promise<AccessLevel | null> {
  const { data, error } = await client
    .schema('portal')
    .rpc('nivel_na_iniciativa', { p_initiative_id: initiativeId })

  if (error) throw new SupabaseError(error.message, error)
  return (data as AccessLevel | null) ?? null
}

export type NivelNaIniciativa = { initiative_id: number; nivel: AccessLevel | null }

/**
 * O nível da pessoa em cada iniciativa que ela enxerga no projeto, numa chamada
 * só (ADR-096). O seletor de iniciativa precisa dizer, para cada uma, se ela
 * abre chamado lá, e o do mover, se ela é Testadora lá.
 */
export async function listMeusNiveisNoProjeto(
  client: SupabaseClient,
  projectId: number,
): Promise<NivelNaIniciativa[]> {
  // biome-ignore lint/suspicious/noExplicitAny: RPC nascida depois da última regeração dos types
  const { data, error } = await (client.schema('portal') as any).rpc('meus_niveis_no_projeto', {
    p_project_id: projectId,
  })

  if (error) throw new SupabaseError(error.message, error)
  return (data ?? []) as NivelNaIniciativa[]
}

/** Quem mais desta empresa tem pelo menos este nível na iniciativa. */
export async function listQuemPodeNaIniciativa(
  client: SupabaseClient,
  initiativeId: number,
  minimo: AccessLevel,
): Promise<ColegaQuePode[]> {
  const { data, error } = await client.schema('portal').rpc('quem_pode_na_iniciativa', {
    p_initiative_id: initiativeId,
    p_minimo: minimo,
  })

  if (error) throw new SupabaseError(error.message, error)
  return (data ?? []) as ColegaQuePode[]
}

/**
 * O dono da conta troca o nível de alguém do time dele neste projeto.
 *
 * O escopo não muda: em quais iniciativas a pessoa entra continua sendo decisão
 * da equipe. A RPC recusa mudar o próprio nível e recusa tirar o último
 * aprovador do projeto.
 */
export async function definirNivelDoColega(
  client: SupabaseClient,
  clientUserId: string,
  projectId: number,
  nivel: AccessLevel,
): Promise<number> {
  const { data, error } = await client.schema('portal').rpc('definir_nivel_do_colega', {
    p_client_user_id: clientUserId,
    p_project_id: projectId,
    p_nivel: nivel,
  })

  if (error) throw new SupabaseError(error.message, error)
  return (data as number) ?? 0
}
